import React, {
  lazy,
  type PropsWithChildren,
  Suspense,
  useEffect,
} from 'react';
import { ColorSchemeScript, mantineHtmlProps } from '@mantine/core';
import { captureException } from '@sentry/tanstackstart-react';
import type { QueryClient } from '@tanstack/react-query';
import type { ErrorComponentProps } from '@tanstack/react-router';
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useRouterState,
} from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';

import MainWrapper from '@app/MainWrapper';
import { NotFound } from '@pages/not-found/ui/NotFound';
import { SomethingWentWrong } from '@pages/something-went-wrong/ui/SomethingWentWrong';
import { optionsSettings } from '@entities/settings/api';
import imgLoading from '@shared/img/loading/loading.svg';

import '../../assets/fonts/fonts.css';
import { checkIsSsr } from '@shared/utils/check-is-ssr';

type TRouterContext = {
  queryClient: QueryClient;
};

const ProviderThemeRedesign = lazy(
  () => import('@app/providers/ProviderThemeRedesign'),
);
const ProviderThemeLegacy = lazy(
  () => import('@app/providers/ProviderThemeLegacy'),
);

// The theme provider renders inside the document `<body>` (here), NOT in the
// router's `Wrap` which sits outside `<html>` — there `MantineProvider`'s
// CssVariables `<style>` becomes a sibling of `<html>` and React 19 refuses it
// ("outside the main document"). Kept lazy so only the active theme's chunk +
// CSS load. Mantine's document-level setup (ColorSchemeScript + mantineHtmlProps)
// must additionally live in the SSR'd shell head/html: a sync script and the
// color-scheme attr have to render in the initial document commit, or React 19
// can't hoist the script and the html attr mismatches on hydration. All gated on
// the redesign flag so the legacy shell stays Mantine-free.
const IS_REDESIGN = import.meta.env.MODE_FEATURE_FLAG_REDESIGN_MANTINE === 'on';
const ProviderTheme = IS_REDESIGN ? ProviderThemeRedesign : ProviderThemeLegacy;

const RootShell = ({ children }: PropsWithChildren) => {
  // Show the "Logging you in…" caption in the pre-hydration loader ONLY on
  // /become, so it is present from the first frame there (matching the Become
  // page's own <Title>) instead of popping in after hydration — while the
  // dashboard reboot stays logo-only. The selected value is a boolean that is
  // constant for the lifetime of a document (routing between /become and the
  // rest only happens via a full page reload), so this never re-renders the
  // shell on in-app navigation and cannot re-insert a removed loader.
  const isBecomeRoute = useRouterState({
    select: (s) => s.location.pathname.startsWith('/become'),
  });

  return (
    <ProviderTheme>
      <html lang='en' {...(IS_REDESIGN ? mantineHtmlProps : {})}>
        <head>
          <meta charSet='utf-8' />
          {IS_REDESIGN && <ColorSchemeScript defaultColorScheme='auto' />}
          <meta
            name='viewport'
            content='width=device-width, initial-scale=1, shrink-to-fit=no'
          />
          <meta name='theme-color' content='#000000' />
          <link rel='manifest' href='/manifest.json' />
          <link
            rel='stylesheet'
            href='https://cdn.linearicons.com/free/1.0.0/icon-font.min.css'
          />
          <link rel='stylesheet' href='/load.css' />
          <HeadContent />
        </head>
        <body>
          {/** biome-ignore lint/correctness/useUniqueElementIds: intentional */}
          <div id='root'>
            {/**
             * Pre-hydration loader: paints on the first SSR frame (styled by
             * /load.css) so full page boots don't flash blank while the bundle
             * and lazy theme chunk load. Removed once React mounts (RootComponent).
             */}
            {/** biome-ignore lint/correctness/useUniqueElementIds: intentional */}
            <div id='app-loader'>
              <img src={imgLoading} alt='' />
              {isBecomeRoute && (
                <p className='app-loader-text'>Logging you in…</p>
              )}
            </div>
            <MainWrapper>
              <Suspense fallback={null}>{children}</Suspense>
            </MainWrapper>
          </div>
          <noscript>You need to enable JavaScript to run this app.</noscript>
          <Scripts />
        </body>
      </html>
    </ProviderTheme>
  );
};

const shouldShowDevtools =
  import.meta.env.MODE !== 'production' &&
  !checkIsSsr() &&
  window.localStorage.getItem('TS_ROUTER_DEVTOOLS') === 'true';

const RootComponent = () => {
  // Remove the pre-hydration loader only once the router has fully settled on
  // the destination route (status 'idle'), NOT merely when the root mounts.
  // Otherwise the loader is torn down while a nested route chunk is still
  // pending, exposing a blank Outlet — very visible in dev (unbundled module
  // waterfall) and a latent flash in prod. Keeping the branded logo up until
  // 'idle' bridges that window on every full page boot.
  const isRouterIdle = useRouterState({ select: (s) => s.status === 'idle' });
  useEffect(() => {
    if (isRouterIdle) {
      document.getElementById('app-loader')?.remove();
    }
  }, [isRouterIdle]);

  return (
    <>
      <Outlet />
      {shouldShowDevtools && <TanStackRouterDevtools />}
    </>
  );
};

/**
 * Root error boundary UI rendering TanStack Router's default error layout.
 *
 * TanStack Router swallows route render errors into its own boundary, so they
 * never reach Sentry's global window handler — report them explicitly here.
 */
const RootErrorComponent = ({ error }: ErrorComponentProps) => {
  useEffect(() => {
    captureException(error);
  }, [error]);

  return <SomethingWentWrong />;
};

export const Route = createRootRouteWithContext<TRouterContext>()({
  /**
   * ssr:false — loader is meant to be client-only. Beware the dev/prod split:
   * - `vite dev` IGNORES ssr:false: loader runs server-side, so handleFetch.server()
   *   fires; it works locally only because PUB_URL is set (spoofs the referer);
   * - the prod build HONORS ssr:false: loader is skipped server-side, settings fetch
   *   runs in the browser via the nitro proxy (forwards the real Referer);
   *   handleFetch.server() never fires — in prod it would hit upstream with no referer
   *   (PUB_URL unset).
   * Net prod effect: first paint / no-JS shows fallback meta; real per-pub title fills
   * after hydration (accepted: dashboard is login-walled).
   * Verify SSR-visible behavior against `vite build`, NEVER `vite dev`. Don't flip to
   * true without proving the prod upstream accepts a referer-less server fetch.
   */
  ssr: false,
  loader: async ({ context }) => {
    try {
      return await context.queryClient.fetchQuery(optionsSettings());
    } catch {
      return null;
    }
  },
  head: ({ loaderData }) => {
    const title = loaderData?.title?.trim() || 'Redtrack.io';
    const favicon = loaderData?.favicon_url?.trim() || '/favicon.png';

    return {
      meta: [
        { title },
        { property: 'og:title', content: title },
        { property: 'og:image', content: favicon },
        { name: 'twitter:title', content: title },
        { name: 'twitter:image', content: favicon },
      ],
      links: [{ rel: 'shortcut icon', href: favicon }],
      ...(loaderData?.gtm_id && {
        scripts: [
          {
            id: 'gtm',
            async: true,
            src: `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(loaderData.gtm_id)}`,
          },
        ],
      }),
    };
  },
  shellComponent: RootShell,
  component: RootComponent,
  errorComponent: RootErrorComponent,
  notFoundComponent: NotFound,
});
